export const VOICE_RECORDING_MAX_DURATION_MS = 30 * 60 * 1000;
export const VOICE_RECORDING_BITS_PER_SECOND = 32_000;

const VOICE_RECORDING_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

const FALLBACK_RECORDING_MIME_TYPE = "audio/webm";

export function selectVoiceRecordingMimeType(
  isTypeSupported: (mimeType: string) => boolean,
): string | null {
  return VOICE_RECORDING_MIME_TYPES.find((mimeType) => isTypeSupported(mimeType)) ?? null;
}

export interface VoiceInputEnvironment {
  readonly isSecureContext: boolean;
  readonly hasMediaRecorder: boolean;
  readonly hasGetUserMedia: boolean;
  readonly queryMicrophonePermission: (() => Promise<unknown>) | null;
}

export function readVoiceInputEnvironment(): VoiceInputEnvironment {
  return {
    isSecureContext: window.isSecureContext,
    hasMediaRecorder: typeof MediaRecorder !== "undefined",
    hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === "function",
    queryMicrophonePermission:
      typeof navigator.permissions?.query === "function"
        ? () => navigator.permissions.query({ name: "microphone" })
        : null,
  };
}

export async function detectVoiceInputSupport(
  environment: VoiceInputEnvironment = readVoiceInputEnvironment(),
): Promise<boolean> {
  if (
    !environment.isSecureContext ||
    !environment.hasMediaRecorder ||
    !environment.hasGetUserMedia ||
    !environment.queryMicrophonePermission
  ) {
    return false;
  }
  try {
    await environment.queryMicrophonePermission();
    return true;
  } catch {
    return false;
  }
}

export async function readMicrophonePermissionState(): Promise<PermissionState> {
  const status = await navigator.permissions.query({ name: "microphone" });
  return status.state;
}

export type MicrophoneAccessFailure = "denied" | "unavailable";

const MICROPHONE_DENIED_ERROR_NAMES = new Set([
  "NotAllowedError",
  "PermissionDeniedError",
  "SecurityError",
]);

export function classifyMicrophoneAccessError(error: unknown): MicrophoneAccessFailure {
  const name = error instanceof Error ? error.name : "";
  return MICROPHONE_DENIED_ERROR_NAMES.has(name) ? "denied" : "unavailable";
}

function stopStreamTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

export async function requestMicrophonePermission(): Promise<"granted" | MicrophoneAccessFailure> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stopStreamTracks(stream);
    return "granted";
  } catch (error) {
    return classifyMicrophoneAccessError(error);
  }
}

export interface VoiceRecording {
  readonly blob: Blob;
  readonly mimeType: string;
  readonly durationMs: number;
}

export interface ActiveVoiceRecording {
  readonly stream: MediaStream;
  readonly mimeType: string;
  readonly stop: () => Promise<VoiceRecording>;
  readonly cancel: () => void;
}

export async function startVoiceRecording(options: {
  readonly onLimitReached: () => void;
  readonly onChunk?: (chunk: Blob) => void;
  readonly chunkIntervalMs?: number;
  readonly maxDurationMs?: number;
}): Promise<ActiveVoiceRecording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const selectedMimeType = selectVoiceRecordingMimeType((mimeType) =>
    MediaRecorder.isTypeSupported(mimeType),
  );
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, {
      audioBitsPerSecond: VOICE_RECORDING_BITS_PER_SECOND,
      ...(selectedMimeType ? { mimeType: selectedMimeType } : {}),
    });
  } catch (error) {
    stopStreamTracks(stream);
    throw error;
  }
  const chunks: Blob[] = [];
  let limitReached = false;
  const reachLimit = () => {
    if (limitReached) return;
    limitReached = true;
    options.onLimitReached();
  };
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) {
      chunks.push(event.data);
      options.onChunk?.(event.data);
    }
  });
  try {
    recorder.start(options.chunkIntervalMs);
  } catch (error) {
    stopStreamTracks(stream);
    throw error;
  }
  const stopped = new Promise<void>((resolve) => {
    recorder.addEventListener("stop", () => resolve(), { once: true });
  });
  const startedAt = performance.now();
  const maxDurationTimer = window.setTimeout(
    reachLimit,
    options.maxDurationMs ?? VOICE_RECORDING_MAX_DURATION_MS,
  );
  const release = () => {
    window.clearTimeout(maxDurationTimer);
    stopStreamTracks(stream);
  };
  const stopRecorder = () => {
    if (recorder.state !== "inactive") {
      recorder.stop();
    }
  };
  return {
    stream,
    mimeType: recorder.mimeType || selectedMimeType || FALLBACK_RECORDING_MIME_TYPE,
    stop: async () => {
      stopRecorder();
      await stopped;
      release();
      const mimeType = recorder.mimeType || selectedMimeType || FALLBACK_RECORDING_MIME_TYPE;
      return {
        blob: new Blob(chunks, { type: mimeType }),
        mimeType,
        durationMs: Math.round(performance.now() - startedAt),
      };
    },
    cancel: () => {
      stopRecorder();
      release();
    },
  };
}
